import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Typography, Alert, CircularProgress, Checkbox, FormControlLabel, Divider } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  CONSENT_COPY_VERSION,
  CONSENT_HEADING,
  CONSENT_BODY,
  CONSENT_CHECKBOX
} from './consentCopy';

/**
 * Pre-join lobby: checks mic/camera access and (for clients only) requires
 * consent to transcription before the Join button enables.
 */
export default function PreJoinLobby({
  title = 'Ready to join?',
  subtitle,
  onJoin,
  joinLabel = 'Join call',
  joining = false,
  requireConsent = false  // set true for the client role
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState('checking');
  const [errorMessage, setErrorMessage] = useState(null);
  const [consented, setConsented] = useState(!requireConsent);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setStatus('ready');
      } catch (err) {
        console.error('[PreJoinLobby] getUserMedia error', err);
        if (cancelled) return;
        setErrorMessage(err.message || 'Microphone / camera access denied');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      const s = streamRef.current;
      if (s) { s.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    };
  }, []);

  const handleJoin = () => onJoin?.({ consentCopyVersion: CONSENT_COPY_VERSION });

  return (
    <Paper sx={{ p: 4, maxWidth: 640, mx: 'auto', my: 6 }}>
      <Typography variant="h5" gutterBottom>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      <Box
        sx={{
          aspectRatio: '16 / 9',
          bgcolor: 'grey.900',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2
        }}
      >
        {status === 'checking' && <CircularProgress sx={{ color: 'common.white' }} />}
        {status !== 'error' && (
          <video ref={videoRef} autoPlay playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
          />
        )}
      </Box>

      {requireConsent && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <LockOutlinedIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
            <Typography variant="subtitle2">{CONSENT_HEADING}</Typography>
          </Box>
          {CONSENT_BODY.map((para, i) => (
            <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {para}
            </Typography>
          ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="body2">{CONSENT_CHECKBOX}</Typography>}
            sx={{ mt: 0.5 }}
          />
        </Box>
      )}

      {status === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage || 'Could not access microphone/camera.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleJoin}
          disabled={status !== 'ready' || joining || (requireConsent && !consented)}
        >
          {joining ? 'Joining…' : joinLabel}
        </Button>
      </Box>
    </Paper>
  );
}
